import React, { memo, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Grid,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon,
  Checklist as ChecklistIcon,
  MenuBook as MenuBookIcon,
  Route as RouteIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import HeroSection from '../components/home/HeroSection';
import { CLARITY_GOLD_TEXT, FONT_DISPLAY, FONT_MONO } from '../constants/brand';
import { useAuth } from '../auth/AuthContext';
import { useAuthModal } from '../auth/AuthModalContext';
import CentralTestimonials from '../components/CentralTestimonials';

// ── Shared section label ────────────────────────────────────────────────────
// A small mono "comment" tag ahead of each section heading — it's the one
// structural device repeated across the page, tying back to the hero's
// terminal motif without ever feeling like decoration for its own sake.

const SectionEyebrow = ({ children }) => (
  <Typography component="p" sx={{ fontFamily: FONT_MONO, fontSize: '0.8rem', color: 'text.secondary', letterSpacing: 0.4, mb: 1.5 }}>
    <Box component="span" sx={{ color: CLARITY_GOLD_TEXT, fontWeight: 600 }}>{'// '}</Box>
    {children}
  </Typography>
);

// ── How it works ───────────────────────────────────────────────────────────────

const STEPS = [
  {
    id: 'pick-path',
    number: '01',
    icon: <RouteIcon />,
    title: 'Create your learner account',
    body: 'Start with a simple account so your progress, journal notes, and assigned cohort can stay linked to you.',
  },
  {
    id: 'work-through-lessons',
    number: '02',
    icon: <MenuBookIcon />,
    title: 'Get your assigned track',
    body: 'Your program admin assigns the cohort and track. Once assigned, everything appears inside My Learning.',
  },
  {
    id: 'build-understanding',
    number: '03',
    icon: <ChecklistIcon />,
    title: 'Build real understanding',
    body: 'Work through lessons, practise with guided frameworks, and track your progress from one dashboard.',
  },
];

const StepCard = memo(function StepCard({ step }) {
  const theme = useTheme();
  return (
    <Stack component="article" aria-label={`Step ${step.number}: ${step.title}`} spacing={2}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box
          aria-hidden
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {step.icon}
        </Box>
        <Typography
          variant="overline"
          fontWeight={700}
          color="primary.main"
          sx={{ fontFamily: FONT_MONO, letterSpacing: 1 }}
        >
          Step {step.number}
        </Typography>
      </Stack>
      <Typography variant="h6" fontWeight={700}>
        {step.title}
      </Typography>
      <Typography variant="body2" color="text.secondary" lineHeight={1.7}>
        {step.body}
      </Typography>
    </Stack>
  );
});

// ── Page ─────────────────────────────────────────────────────────────────────

const HomePage = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const { openAuthModal } = useAuthModal();

  // Handles arriving at "/#how-it-works" from another page (e.g. the hero's
  // CTA falling back to navigate() when it can't find the section on-page).
  useEffect(() => {
    if (window.location.hash === '#how-it-works') {
      const target = document.getElementById('how-it-works');
      if (target) {
        requestAnimationFrame(() => target.scrollIntoView({ behavior: 'smooth', block: 'start' }));
      }
    }
  }, []);

  return (
    <Box>
      <HeroSection />

      {/* How it works */}
      <Box
        component="section"
        id="how-it-works"
        aria-labelledby="how-it-works-heading"
        sx={{ py: { xs: 8, md: 12 }, scrollMarginTop: { xs: 72, md: 88 } }}
      >
        <Container maxWidth="lg">
          <Box textAlign="center" mb={6}>
            <SectionEyebrow>the process</SectionEyebrow>
            <Typography id="how-it-works-heading" variant="h3" fontWeight={800} sx={{ fontFamily: FONT_DISPLAY }} gutterBottom>
              How it works
            </Typography>
            <Typography variant="h6" color="text.secondary" fontWeight={400}>
              Three steps from account to assigned lessons.
            </Typography>
          </Box>

          <Grid container spacing={5} role="list">
            {STEPS.map((step, index) => (
              <Grid
                item
                xs={12}
                md={4}
                key={step.id}
                role="listitem"
                sx={
                  index < STEPS.length - 1
                    ? { borderRight: { md: `1px dashed ${alpha(theme.palette.text.primary, 0.15)}` } }
                    : undefined
                }
              >
                <StepCard step={step} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <CentralTestimonials platform="QODE" />

      {/* Bottom CTA */}
      <Box
        component="section"
        aria-labelledby="cta-heading"
        sx={{
          py: { xs: 8, md: 10 },
          bgcolor: alpha(theme.palette.primary.main, 0.04),
          borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        }}
      >
        <Container maxWidth="sm">
          <Stack alignItems="center" spacing={3} textAlign="center">
            <SectionEyebrow>ready when you are</SectionEyebrow>
            <Typography id="cta-heading" variant="h3" fontWeight={800} sx={{ fontFamily: FONT_DISPLAY }}>
              Create your account and start from My Learning
            </Typography>
            <Typography variant="h6" color="text.secondary" fontWeight={400}>
              Create an account now. Your assigned cohort and track will appear when the program admin adds them.
            </Typography>
            {user ? (
              <Button
                component={RouterLink}
                to="/my-learning"
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                sx={{ px: 5, py: 1.5, fontWeight: 700, fontSize: '1rem' }}
              >
                Go to my learning
              </Button>
            ) : (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => openAuthModal('register')}
                  sx={{ px: 5, py: 1.5, fontWeight: 700, fontSize: '1rem' }}
                >
                  Create account
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => openAuthModal('login')}
                  sx={{ px: 5, py: 1.5, fontWeight: 700, fontSize: '1rem' }}
                >
                  Sign in
                </Button>
              </Stack>
            )}
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage;
